import { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { requestPOST } from 'src/utils/baseAPI';
import moment from 'moment';
import { useIntl } from 'react-intl';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const RenderChartBarrierManual = ({ fromDateToDate, selectedBranch }) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const intl = useIntl();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Lấy dữ liệu mở barrier tùy ý
        const res = await requestPOST('api/v1/eventvehicles/search', {
          timeFrom: fromDateToDate?.fromDate.clone().add(7, 'hours'),
          timeTo: fromDateToDate?.toDate.clone().add(7, 'hours'),
          branchId: selectedBranch ?? null,
          note: "<Mở tùy ý>",
          pageNumber: 1,
          pageSize: 1000, // Lấy nhiều dữ liệu để tính toán
        });

        const data = res?.data ?? [];
        setTotalCount(res?.totalCount ?? 0);
        
        // Nhóm dữ liệu theo ngày và direction (IN/OUT)
        const groupedByDate = {};
        
        data.forEach(record => {
          const eventDate = record.dateTimeEvent ? moment(record.dateTimeEvent).format('DD/MM/YYYY') : null;
          
          if (eventDate) {
            if (!groupedByDate[eventDate]) {
              groupedByDate[eventDate] = { barrierIn: 0, barrierOut: 0, total: 0 };
            }
            
            // Đếm barrier mở vào và ra dựa trên field laneDirection
            if (record.laneDirection === 'IN') {
              groupedByDate[eventDate].barrierIn++;
            } else if (record.laneDirection === 'OUT') {
              groupedByDate[eventDate].barrierOut++;
            }
            groupedByDate[eventDate].total++;
          }
        });

        // Chuyển đổi thành dữ liệu cho biểu đồ
        const dates = Object.keys(groupedByDate).sort((a, b) => 
          moment(a, 'DD/MM/YYYY').diff(moment(b, 'DD/MM/YYYY'))
        );
        
        const barrierInData = dates.map(date => groupedByDate[date].barrierIn);
        const barrierOutData = dates.map(date => groupedByDate[date].barrierOut);

        setChartData({
          labels: dates,
          datasets: [
            {
              label: 'Mở barrier vào',
              data: barrierInData,
              backgroundColor: 'rgba(54, 162, 235, 0.8)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 1,
            },
            {
              label: 'Mở barrier ra',
              data: barrierOutData,
              backgroundColor: 'rgba(255, 99, 132, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1,
            },
          ],
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching barrier manual data:', error);
        setLoading(false);
      }
    };

    if (fromDateToDate?.fromDate && fromDateToDate?.toDate) {
      fetchData();
    }
  }, [fromDateToDate, selectedBranch]);

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Thống kê mở barrier tùy ý (${fromDateToDate?.fromDate?.format('DD/MM/YYYY')} - ${fromDateToDate?.toDate?.format('DD/MM/YYYY')})`,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value + ' lượt';
          }
        }
      },
      x: {
        title: {
          display: true,
          text: 'Ngày'
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  return (
    <div className="card card-xl-stretch mb-xl-5">
      <div className="card-header min-h-50px">
        <h3 className="card-title fw-bold text-primary">Thống kê mở barrier tùy ý</h3>
        <div className="card-toolbar">
          <span className="badge badge-light-primary fs-7">
            Tổng: {totalCount} lượt
          </span>
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <Bar options={options} data={chartData} />
        )}
      </div>
    </div>
  );
};

export default RenderChartBarrierManual; 